import { apiService } from "../../common/api/apiService";
import { API_ENDPOINTS } from "../../common/api/apiEndpoints";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LOADING_STATUS } from "../../common/constants";
import { RootState } from "../../app/store";

export enum BookingType {
  /// <summary>
  /// The Preliminary booking.
  /// </summary>
  Preliminary = 0,
  /// <summary>
  /// The normal booking.
  /// </summary>
  Normal = 1,
  /// <summary>
  /// The TravelAgency booking.
  /// </summary>
  TravelAgency = 2,
  /// <summary>
  /// The UnConfirmedInternet booking.
  /// </summary>
  UnConfirmedInternet = 3,
  /// <summary>
  /// The ConfirmedInternet booking.
  /// </summary>
  ConfirmedInternet = 4,
  /// <summary>
  /// The OwnerOwn booking.
  /// </summary>
  OwnerOwn = 5,
  /// <summary>
  /// The OwnerReported booking.
  /// </summary>
  OwnerReported = 6,

  /// <summary>
  /// The PreliminaryGroup booking.
  /// </summary>
  PreliminaryGroup = 7,

  /// <summary>
  /// The ConfirmedGroup booking.
  /// </summary>
  ConfirmedGroup = 8,

  /// <summary>
  /// The UnConfirmedInternetOwnerSite booking.
  /// </summary>
  UnConfirmedInternetOwnerSite = 9,

  /// <summary>
  /// The ConfirmedInternetOwnerSite booking.
  /// </summary>
  ConfirmedInternetOwnerSite = 10,

  /// <summary>
  /// The Interhome booking.
  /// </summary>
  UNCONFIRMEDINTERHOME = 11,

  /// <summary>
  /// The Interhome booking.
  /// </summary>
  CONFIRMEDINTERHOME = 12,

  /// <summary>
  /// The TemporaryDisabled booking.
  /// </summary>
  TemporaryDisabled = 13,

  /// <summary>
  /// The None booking.
  /// </summary>
  None = 14,

  /// <summary>
  /// The Ical bloked period.
  /// </summary>
  Ical = 998,
}
export function GetBookingCalendarLabel(bookingType: BookingType) {
  let bookingLabel: string;
  switch (bookingType) {
    case BookingType.Preliminary:
      bookingLabel = "A";
      break;
    case BookingType.Normal:
      bookingLabel = "N";
      break;
    case BookingType.TravelAgency:
      bookingLabel = "N";
      break;
    case BookingType.ConfirmedInternet:
    case BookingType.CONFIRMEDINTERHOME:
    case BookingType.ConfirmedInternetOwnerSite:
      bookingLabel = "I";
      break;
    case BookingType.OwnerOwn:
      bookingLabel = "O";
      break;
    case BookingType.OwnerReported:
    case BookingType.UnConfirmedInternetOwnerSite:
      bookingLabel = "Y";
      break;
    case BookingType.PreliminaryGroup:
    case BookingType.ConfirmedGroup:
      bookingLabel = "R";
      break;
    case BookingType.TemporaryDisabled:
      bookingLabel = "V";
      break;
    case BookingType.Ical:
      bookingLabel = "iC";
      break;
    default:
      bookingLabel = "1";
      break;
  }
  return bookingLabel;
}
export interface BookingAddonItem {
  units: number;
  productName: string;
}
export interface AccommodationBookingItem {
  accommodationId: string;
  bookingItems: BookingItem[];
}

export interface BookingItem {
  id: string;
  accommodationId?: string;
  bookingType: BookingType;
  bookingNumber: number;
  payerFirstName: string;
  payerLastName: string;
  startDate: string;
  startDateString: string;
  endDate: string;
  endDateString: string;
  adults: number;
  children: number;
  bookingsAddonItems: BookingAddonItem[];
  customerBookingNotes: string;
  ownerBookingNotes: string;
  itemName?: string;
  isCancelled: boolean;
  extraBookingResponses?: [BookingItem];
  iCalDescription?: string;
  iCalExternalUid?: string;
  iCalFullDescription?: string;
  iCalLocation?: string;
  iCalImportId?: string;
  iCalDistributionChannelName?: string;
  checkInTime?: string;
  checkOutTime?: string;
}

export interface ReservedDate {
  bookingId: string;
  bookingType: number;
  startDay: boolean;
  endDay: boolean;
  adults?: number;
  children?: number;
  ownerBookingNotes?: string;
  startDateString?: string;
  endDateString?: string;
  iCalDescription?: string;
  iCalExternalUid?: string;
  iCalFullDescription?: string;
  iCalLocation?: string;
  iCalImportId?: string;
  iCalDistributionChannelName?: string;
  checkInTime?: string;
  checkOutTime?: string;
}

export interface BookingDetailsItem {
  id: string;
  bookingType: BookingType;
  bookingNumber: number;
  payerFirstName: string;
  payerLastName: string;
  startDate: string;
  startDateString: string;
  endDate: string;
  endDateString: string;
  adults: number;
  children: number;
}
export interface OptiwattiData {
  accommodationBookingOrderItemId: string;
  eventGuid: string;
  status: string;
  message: string;
  translatedStatus: string;
}
export interface PurchasePriceItem {
  sitePurchasePrice: number;
  ownerPurchasePrice: number;
  sitePurchasePriceVat: number;
  ownerPurchasePriceVat: number;
}
export interface PricesItem {
  grossPrice: number;
  grossPriceVat: number;
  finalBillNetPrice: number;
  netPrice: number;
  finalBillPrice: number;
  discount: number;
  discountP: number;
  comission: number;
  comissionP: number;
  travelAgencyComission: number;
  travelAgencyComissionP: number;
  sellerCommission: number;
  finalBillPriceWithoutVat: number;
  finalBillPriceVat: number;
  advanceBillPrice: number;
  advanceBillPriceVat: number;
  advanceBillPriceWithoutVat: number;
  vatP: number;
}
export interface BookingDetailsOrderItem {
  id?: string;
  orderItemType: number;
  accommodationId: string;
  accommodationNumber: number;
  arrivalDateTime: string;
  departureDateTime: string;
  startDateTime: string;
  endDateTime: string;
  itemName: string;
  optiwattiData?: OptiwattiData;
  pricesResponse: PricesItem;
  purchasePricesResponse: PurchasePriceItem;
  includedInPrice: boolean;
  amountOfAdults: number;
  amountOfChildren: number;
  units: number;
  bookingTotalBuyPrice: number;
  realOwnerAccountId?: string;
  ownerAccountId?: string;
}
export interface BookingDetailsNoteItem {
  noteText: string;
}
export interface ExtraBookingResponse {
  id: string;
  bookingNumber: string;
  createdAt: string;
}
export interface BookingDetails {
  id: string;
  payerId: string;
  bookingType: number;
  bookingNumber: number;
  payerFirstName: string;
  payerLastName: string;
  accommodationId: string;
  isCancelled: boolean;
  bookingOrderItems: BookingDetailsOrderItem[];
  customerBookingNotes: BookingDetailsNoteItem[];
  ownerBookingNotes: BookingDetailsNoteItem[];
  officerBookingNotes: BookingDetailsNoteItem[];
  finalBillPrice: number;
  finalBillPriceWithoutVat: number;
  finalBillPriceVat: number;
  advanceBillPrice: number;
  advanceBillPriceVat: number;
  advanceBillPriceWithoutVat: number;
  bookingContainerId: string;
  billingLocale: number;
  finalClient?: string;
  finalClientCompany?: string;
  finalClientStreetAddress?: string;
  finalClientZipCode?: string;
  finalClientCity?: string;
  finalClientCountry?: string;
  finalClientEmail?: string;
  finalClientPhone?: string;
  extraBookingResponses: ExtraBookingResponse[];
}

export interface WebShopCustomerItem {
  customerNumber: string;
  firstName: string;
  streetAddress: string;
  additionalAddress: string;
  postalCode: string;
  postCity: string;
  countryId: string;
  phoneNumber: string;
  email: string;
}

export interface BookingContainerUpdateItem {
  bookingContainerId: string;
  bookings: BookingDetails[];
  advancePaid: boolean;
  acceptMarketing: boolean;
  acceptCancelInsuranceTerms: boolean;
  acceptBookingTerms: boolean;
  adults: number;
  childs: number;
  bookingContainerStatus: number;
}

export interface BookingContainer {
  id: string;
  bookingResponses: BookingDetails[];
  webShopCustomer: WebShopCustomerItem;
  advancePaid: boolean;
  acceptMarketing: boolean;
  acceptCancelInsuranceTerms: boolean;
  acceptBookingTerms: boolean;
  siteId: string;
  adults: number;
  childs: number;
  bookingContainerStatus: number;
}
export interface BookingsRequest {
  accommodationId: string;
  ownerId: string;
  isOwnerUser: boolean;
  bookingsStartDate: string;
  bookingsEndDate: string;
}
export interface CalendarBookingsBatchRequest {
  accommodationIds: string[];
  ownerId: string;
  isOwnerUser: boolean;
  bookingsStartDate: string;
  bookingsEndDate: string;
}
export interface BookingsByIdsRequest {
  accommodationId: string;
  ownerId: string;
  isOwnerUser: boolean;
  bookingIds: string[];
}
export interface ArrivalsAndDeparturesRequest {
  accommodationId: string;
  ArrivalDateTime: string;
  DepartureDateTime: string;
}
export interface DailyArrivalOrDepartureItemAddon {
  addonName: string;
  units: number;
}
export interface DailyArrivalOrDepartureItemExtraBooking {
  extraBookingNumber: number;
  bookingNotes?: string;
  dailyArrivalOrDepartureItemAddons?: DailyArrivalOrDepartureItemAddon[];
}
export interface DailyArrivalOrDepartureItem {
  bookingId: string;
  bookingNumber: number;
  startDateString?: string;
  endDateString?: string;
  arrivalTime: string;
  departureTime: string;
  customerNumber: number;
  customerFirstName: string;
  customerLastName: string;
  customerPhoneNumber?: string;
  adults: number;
  children: number;
  dailyArrivalItemAddons?: DailyArrivalOrDepartureItemAddon[];
  dailyArrivalOrDepartureItemExtraBookings?: DailyArrivalOrDepartureItemExtraBooking[];
  bookingNotes?: string;
  ownerBookingNotes?: string;
  iCalDescription?: string;
  iCalExternalUid?: string;
  iCalFullDescription?: string;
  iCalLocation?: string;
  iCalImportId?: string;
  iCalDistributionChannelName?: string;
  checkInTime?: string;
  checkOutTime?: string;
  bookingTypeEnum: BookingType;
}
export interface DailyArrivalsAndDeparturesItem {
  date: string;
  accommodationNumber: number;
  accommodationName: string;
  accommodationQualityRating: number;
  keyBox: string;
  accommodationId: string;
  arrivalItem: DailyArrivalOrDepartureItem;
  departureItem: DailyArrivalOrDepartureItem;
}
export interface DailyArrivalsAndDepartures {
  dailyArrivalsAndDepartures: DailyArrivalsAndDeparturesItem[];
}
export interface BookingsState {
  accommodationsBookingItems: [];
  accommodationsCalendarBookingItems: [];
  bookingItems: BookingItem[];
  bookingItem: BookingContainer;
  updatedBookingItem: BookingContainer;
  extraBookingItems: BookingDetails[];
  arrivalsAndDeparturesItem: DailyArrivalsAndDepartures;
  bookingItemsStartingInSevenDays: BookingItem[];
  bookingItemsCreatedInSevenDays: BookingItem[];
  bookingItemStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  extraBookingItemStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingsItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  updateBookingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  arrivalsAndDeparturesItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingsStartingInSevenDaysStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  bookingsCreatedInSevenDaysStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

export interface AddBookingOwnerNote {
  ownerId: string;
  bookingId: string;
  ownerBookingNote: string;
}

export interface CancelBookingCommand {
  bookingId: string;
  reasonOfCancellation: string;
}

export interface OwnerOwnBookingNote {
  NoteText: string;
}

export interface OwnerOwnBooking {
  ownerId: string;
  AccommodationId: string;
  ArrivalDateTime: string;
  DepartureDateTime: string;
  OwnerBookingNote: OwnerOwnBookingNote;
  Adults: number;
  Childs: number;
  BookingType: string;
}

export interface UpdateOwnerOwnBooking {
  bookingId: string;
  ownerId: string;
  AccommodationId: string;
  ArrivalDateTime: string;
  DepartureDateTime: string;
  OwnerBookingNote: OwnerOwnBookingNote;
  Adults: number;
  Childs: number;
  BookingType: string;
}
export const getAccommodationsBookingsAsync = createAsyncThunk(
  "bookings/getAccommodationBookings",
  async (bookingsRequest: BookingsRequest, { rejectWithValue }) => {
    let accommodationBookingItem: AccommodationBookingItem = new (class
      implements AccommodationBookingItem
    {
      accommodationId: string;
      bookingItems: [];
    })();
    accommodationBookingItem.accommodationId = bookingsRequest.accommodationId;

    accommodationBookingItem.bookingItems = await apiService.post(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS,
      bookingsRequest,
    );
    return { accommodationBookingItem };
  },
);

export const getAccommodationsBookingsByIdsAsync = createAsyncThunk(
  "bookings/getAccommodationBookingsByIds",
  async (request: BookingsByIdsRequest, { rejectWithValue }) => {
    let accommodationBookingItem: AccommodationBookingItem = new (class
      implements AccommodationBookingItem
    {
      accommodationId: string;
      bookingItems: [];
    })();
    accommodationBookingItem.accommodationId = request.accommodationId;

    accommodationBookingItem.bookingItems = await apiService.post(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS_BY_IDS,
      request,
    );
    return { accommodationBookingItem };
  },
);

// Lightweight calendar bookings
export const getAccommodationsCalendarBookingsAsync = createAsyncThunk(
  "bookings/getAccommodationCalendarBookings",
  async (bookingsRequest: BookingsRequest, { rejectWithValue }) => {
    const response = await apiService.post(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS_FOR_CALENDAR,
      bookingsRequest,
    );

    // Map lightweight response to BookingItem[] minimally required by calendar
    const mapped = (response as any[]).map((b) => {
      const item: BookingItem = {
        id: b.id ?? b.Id,
        accommodationId:
          b.accommodationId ??
          b.AccommodationId ??
          bookingsRequest.accommodationId,
        bookingType: (b.bookingType ?? b.BookingType) as BookingType,
        bookingNumber: b.bookingNumber ?? b.BookingNumber,
        payerFirstName: "",
        payerLastName: "",
        startDate: b.startDateString ?? b.StartDateString ?? "",
        startDateString: b.startDateString ?? b.StartDateString ?? "",
        endDate: b.endDateString ?? b.EndDateString ?? "",
        endDateString: b.endDateString ?? b.EndDateString ?? "",
        adults: b.adults ?? b.Adults ?? 0,
        children: b.children ?? b.Children ?? 0,
        bookingsAddonItems: [],
        customerBookingNotes:
          b.customerBookingNotes ?? b.CustomerBookingNotes ?? "",
        ownerBookingNotes: b.ownerBookingNotes ?? b.OwnerBookingNotes ?? "",
        isCancelled: false,
        iCalDescription: b.iCalDescription ?? b.ICalDescription ?? "",
        iCalExternalUid: b.iCalExternalUid ?? b.ICalExternalUid ?? "",
        iCalFullDescription:
          b.iCalFullDescription ?? b.ICalFullDescription ?? "",
        iCalLocation: b.iCalLocation ?? b.ICalLocation ?? "",
        iCalImportId: b.iCalImportId ?? b.ICalImportId ?? "",
        iCalDistributionChannelName:
          b.iCalDistributionChannelName ?? b.ICalDistributionChannelName ?? "",
        checkInTime: b.checkInTime ?? b.CheckInTime ?? "",
        checkOutTime: b.checkOutTime ?? b.CheckOutTime ?? "",
      };
      return item;
    });

    let accommodationBookingItem: AccommodationBookingItem = {
      accommodationId: bookingsRequest.accommodationId,
      bookingItems: mapped as any,
    };

    return { accommodationBookingItem };
  },
);

export const getAccommodationsCalendarBookingsBatchAsync = createAsyncThunk(
  "bookings/getAccommodationCalendarBookingsBatch",
  async (request: CalendarBookingsBatchRequest, { rejectWithValue }) => {
    const response = await apiService.post(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS_FOR_CALENDAR_BATCH,
      request,
    );

    const normalizeId = (value: unknown): string => {
      if (typeof value !== "string") {
        return "";
      }
      return value.trim().toLowerCase();
    };

    const grouped = (response as any[]).reduce(
      (acc, b) => {
        const accommodationIdRaw =
          b.accommodationId ?? b.AccommodationId ?? b.accommodationID;
        const accommodationId = normalizeId(accommodationIdRaw);
        if (!accommodationId) {
          return acc;
        }
        if (!acc[accommodationId]) {
          acc[accommodationId] = [];
        }
        const item: BookingItem = {
          id: b.id ?? b.Id,
          accommodationId: (accommodationIdRaw as string) ?? "",
          bookingType: (b.bookingType ?? b.BookingType) as BookingType,
          bookingNumber: b.bookingNumber ?? b.BookingNumber,
          payerFirstName: "",
          payerLastName: "",
          startDate: b.startDateString ?? b.StartDateString ?? "",
          startDateString: b.startDateString ?? b.StartDateString ?? "",
          endDate: b.endDateString ?? b.EndDateString ?? "",
          endDateString: b.endDateString ?? b.EndDateString ?? "",
          adults: b.adults ?? b.Adults ?? 0,
          children: b.children ?? b.Children ?? 0,
          bookingsAddonItems: [],
          customerBookingNotes:
            b.customerBookingNotes ?? b.CustomerBookingNotes ?? "",
          ownerBookingNotes: b.ownerBookingNotes ?? b.OwnerBookingNotes ?? "",
          isCancelled: false,
          iCalDescription: b.iCalDescription ?? b.ICalDescription ?? "",
          iCalExternalUid: b.iCalExternalUid ?? b.ICalExternalUid ?? "",
          iCalFullDescription:
            b.iCalFullDescription ?? b.ICalFullDescription ?? "",
          iCalLocation: b.iCalLocation ?? b.ICalLocation ?? "",
          iCalImportId: b.iCalImportId ?? b.ICalImportId ?? "",
          iCalDistributionChannelName:
            b.iCalDistributionChannelName ??
            b.ICalDistributionChannelName ??
            "",
          checkInTime: b.checkInTime ?? b.CheckInTime ?? "",
          checkOutTime: b.checkOutTime ?? b.CheckOutTime ?? "",
        };
        acc[accommodationId].push(item);
        return acc;
      },
      {} as Record<string, BookingItem[]>,
    );

    const accommodationBookingItems: AccommodationBookingItem[] =
      request.accommodationIds.map((accommodationId) => ({
        accommodationId,
        bookingItems: grouped[normalizeId(accommodationId)] ?? [],
      }));

    return { accommodationBookingItems };
  },
);

export const getBookingsAsync = createAsyncThunk(
  "bookings/getBookings",
  async (bookingsRequest: BookingsRequest, { rejectWithValue }) => {
    const bookings = await apiService.post(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS,
      bookingsRequest,
    );
    return { bookings };
  },
);

export const getBookingDetailsAsync = createAsyncThunk(
  "bookings/getBookingDetails",
  async (bookingId: string, { rejectWithValue }) => {
    const bookingResult = await apiService.get(
      API_ENDPOINTS.BOOKING.GET_BOOKING + "?bookingID=" + bookingId,
    );
    return { bookingResult };
  },
);

export const getExtraBookingDetailsAsync = createAsyncThunk(
  "bookings/getExtraBookingDetails",
  async (bookingId: string, { rejectWithValue }) => {
    const bookingResult = await apiService.get(
      API_ENDPOINTS.BOOKING.GET_BOOKING + "?bookingID=" + bookingId,
    );
    return { bookingResult };
  },
);

export const saveBookingOwnerNoteAsync = createAsyncThunk(
  "bookings/saveBookingOwnerNoteAsync",
  async (bookingOwnerNote: AddBookingOwnerNote, { rejectWithValue }) => {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.BOOKING.SAVE_BOOKING_OWNERNOTE,
        bookingOwnerNote,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const cancelOwnersOwnBookingAsync = createAsyncThunk(
  "bookings/cancelOwnersOwnBooking",
  async (
    bookingCancellationCommand: CancelBookingCommand,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.BOOKING.CANCEL_BOOKING +
          "?bookingId=" +
          bookingCancellationCommand.bookingId +
          "&reasonOfCancellation=" +
          encodeURIComponent(bookingCancellationCommand.reasonOfCancellation),
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveOwnerOwnBookingAsync = createAsyncThunk(
  "bookings/saveOwnerOwnBooking",
  async (ownerOwnBooking: OwnerOwnBooking, { rejectWithValue }) => {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.BOOKING.SAVE_OWNER_OWN_BOOKING,
        ownerOwnBooking,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateOwnerBookingAsync = createAsyncThunk(
  "bookings/updateOwnerBookingAsync",
  async (
    ownerOwnBookingUpdateItem: UpdateOwnerOwnBooking,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.BOOKING.UPDATE_OWNER_OWN_BOOKING,
        ownerOwnBookingUpdateItem,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export interface SaveICalEventDetailsRequest {
  blockedPeriodId: string;
  accommodationId: string;
  adults: number;
  children: number;
  ownerBookingNotes: string;
  checkInTime: string;
  checkOutTime: string;
}

export const saveICalEventDetailsAsync = createAsyncThunk(
  "bookings/saveICalEventDetails",
  async (
    saveICalEventDetailsRequest: SaveICalEventDetailsRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.BLOCKEDPERIOD.SAVE_BLOCKEDPERIOD_BOOKING_DETAILS,
        saveICalEventDetailsRequest,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getArrivalsAndDeparturesAsync = createAsyncThunk(
  "bookings/getArrivalsAndDepartures",
  async (
    arrivalsAndDeparturesRequest: ArrivalsAndDeparturesRequest,
    { rejectWithValue },
  ) => {
    const arrivalsAndDepartures = await apiService.get(
      API_ENDPOINTS.BOOKING.GET_ARRIVALS_AND_DEPARTURES +
        "?accommodationId=" +
        arrivalsAndDeparturesRequest.accommodationId +
        "&ArrivalDateTime=" +
        arrivalsAndDeparturesRequest.ArrivalDateTime +
        "&DepartureDateTime=" +
        arrivalsAndDeparturesRequest.DepartureDateTime,
    );
    return { arrivalsAndDepartures };
  },
);

export const getBookingsStartingInSevenDays = createAsyncThunk(
  "bookings/getBookingsStartingInSevenDays",
  async () => {
    const bookingsStartingInSevenDays = await apiService.get(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS_STARTING_IN_SEVEN_DAYS,
    );
    return { bookingsStartingInSevenDays };
  },
);

export const getBookingsCreatedInSevenDays = createAsyncThunk(
  "bookings/getBookingsCreatedInSevenDays",
  async () => {
    const bookingsCreatedInSevenDays = await apiService.get(
      API_ENDPOINTS.BOOKING.GET_BOOKINGS_CREATED_IN_SEVEN_DAYS,
    );
    return { bookingsCreatedInSevenDays };
  },
);

const initialState: BookingsState = {
  accommodationsBookingItems: [],
  accommodationsCalendarBookingItems: [],
  bookingItems: [],
  bookingsStartingInSevenDaysStatus: LOADING_STATUS.IDLE,
  bookingsCreatedInSevenDaysStatus: LOADING_STATUS.IDLE,
  bookingItemsStartingInSevenDays: [],
  bookingItemsCreatedInSevenDays: [],
  bookingItem: null,
  extraBookingItems: [],
  arrivalsAndDeparturesItem: null,
  bookingItemStatus: LOADING_STATUS.IDLE,
  extraBookingItemStatus: LOADING_STATUS.IDLE,
  bookingsItemsStatus: LOADING_STATUS.IDLE,
  arrivalsAndDeparturesItemsStatus: LOADING_STATUS.IDLE,
  updateBookingStatus: LOADING_STATUS.IDLE,
  updatedBookingItem: null,
};

const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    clearExtraBookingItems: (state) => {
      state.extraBookingItems = [];
      state.extraBookingItemStatus = LOADING_STATUS.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBookingsAsync.pending, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingsAsync.fulfilled, (state, action) => {
        state.bookingsItemsStatus = LOADING_STATUS.IDLE;
        state.bookingItems = action.payload.bookings;
      })
      .addCase(getBookingsAsync.rejected, (state) => {
        state.bookingItemStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingDetailsAsync.pending, (state) => {
        state.bookingItemStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingDetailsAsync.fulfilled, (state, action) => {
        state.bookingItemStatus = LOADING_STATUS.IDLE;
        state.bookingItem = action.payload.bookingResult.result;
      })
      .addCase(getBookingDetailsAsync.rejected, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getExtraBookingDetailsAsync.pending, (state) => {
        state.extraBookingItemStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getExtraBookingDetailsAsync.fulfilled, (state, action) => {
        state.extraBookingItemStatus = LOADING_STATUS.IDLE;
        if (
          (state.extraBookingItems as BookingDetails[]).find(
            (a) => a.id === action.payload.bookingResult.result.id,
          ) === undefined
        ) {
          (state.extraBookingItems as BookingDetails[]).push(
            action.payload.bookingResult.result,
          );
        }
        //state.extraBookingItems = action.payload.bookingResult.result;
      })
      .addCase(getExtraBookingDetailsAsync.rejected, (state) => {
        state.extraBookingItemStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationsBookingsAsync.pending, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAccommodationsBookingsAsync.fulfilled, (state, action) => {
        state.bookingsItemsStatus = LOADING_STATUS.IDLE;
        if (
          (state.accommodationsBookingItems as AccommodationBookingItem[]).find(
            (a) =>
              a.accommodationId ===
              action.payload.accommodationBookingItem.accommodationId,
          ) !== undefined
        ) {
          (state.accommodationsBookingItems as AccommodationBookingItem[]).find(
            (a) =>
              a.accommodationId ===
              action.payload.accommodationBookingItem.accommodationId,
          ).bookingItems = action.payload.accommodationBookingItem.bookingItems;
        } else {
          (state.accommodationsBookingItems as AccommodationBookingItem[]).push(
            action.payload.accommodationBookingItem,
          );
        }
      })
      .addCase(getAccommodationsBookingsByIdsAsync.pending, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAccommodationsBookingsByIdsAsync.fulfilled,
        (state, action) => {
          state.bookingsItemsStatus = LOADING_STATUS.IDLE;
          if (
            (
              state.accommodationsBookingItems as AccommodationBookingItem[]
            ).find(
              (a) =>
                a.accommodationId ===
                action.payload.accommodationBookingItem.accommodationId,
            ) !== undefined
          ) {
            (
              state.accommodationsBookingItems as AccommodationBookingItem[]
            ).find(
              (a) =>
                a.accommodationId ===
                action.payload.accommodationBookingItem.accommodationId,
            ).bookingItems =
              action.payload.accommodationBookingItem.bookingItems;
          } else {
            (
              state.accommodationsBookingItems as AccommodationBookingItem[]
            ).push(action.payload.accommodationBookingItem);
          }
        },
      )
      .addCase(getAccommodationsBookingsByIdsAsync.rejected, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationsCalendarBookingsAsync.pending, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAccommodationsCalendarBookingsAsync.fulfilled,
        (state, action) => {
          state.bookingsItemsStatus = LOADING_STATUS.IDLE;
          if (
            (
              state.accommodationsCalendarBookingItems as AccommodationBookingItem[]
            ).find(
              (a) =>
                a.accommodationId ===
                action.payload.accommodationBookingItem.accommodationId,
            ) !== undefined
          ) {
            (
              state.accommodationsCalendarBookingItems as AccommodationBookingItem[]
            ).find(
              (a) =>
                a.accommodationId ===
                action.payload.accommodationBookingItem.accommodationId,
            ).bookingItems =
              action.payload.accommodationBookingItem.bookingItems;
          } else {
            (
              state.accommodationsCalendarBookingItems as AccommodationBookingItem[]
            ).push(action.payload.accommodationBookingItem);
          }
        },
      )
      .addCase(getAccommodationsCalendarBookingsAsync.rejected, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationsCalendarBookingsBatchAsync.pending, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getAccommodationsCalendarBookingsBatchAsync.fulfilled,
        (state, action) => {
          state.bookingsItemsStatus = LOADING_STATUS.IDLE;
          action.payload.accommodationBookingItems.forEach((incoming) => {
            const existing = (
              state.accommodationsCalendarBookingItems as AccommodationBookingItem[]
            ).find((a) => a.accommodationId === incoming.accommodationId);

            if (existing !== undefined) {
              existing.bookingItems = incoming.bookingItems;
            } else {
              (
                state.accommodationsCalendarBookingItems as AccommodationBookingItem[]
              ).push(incoming);
            }
          });
        },
      )
      .addCase(
        getAccommodationsCalendarBookingsBatchAsync.rejected,
        (state) => {
          state.bookingsItemsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(getArrivalsAndDeparturesAsync.pending, (state) => {
        state.arrivalsAndDeparturesItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getArrivalsAndDeparturesAsync.fulfilled, (state, action) => {
        state.arrivalsAndDeparturesItemsStatus = LOADING_STATUS.IDLE;
        state.arrivalsAndDeparturesItem =
          action.payload.arrivalsAndDepartures.result;
      })
      .addCase(getArrivalsAndDeparturesAsync.rejected, (state) => {
        state.arrivalsAndDeparturesItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingsStartingInSevenDays.pending, (state) => {
        state.bookingsStartingInSevenDaysStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingsStartingInSevenDays.fulfilled, (state, action) => {
        state.bookingsStartingInSevenDaysStatus = LOADING_STATUS.IDLE;
        state.bookingItemsStartingInSevenDays =
          action.payload.bookingsStartingInSevenDays.data;
      })
      .addCase(getBookingsStartingInSevenDays.rejected, (state) => {
        state.bookingsStartingInSevenDaysStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getBookingsCreatedInSevenDays.pending, (state) => {
        state.bookingsCreatedInSevenDaysStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getBookingsCreatedInSevenDays.fulfilled, (state, action) => {
        state.bookingsCreatedInSevenDaysStatus = LOADING_STATUS.IDLE;
        state.bookingItemsCreatedInSevenDays =
          action.payload.bookingsCreatedInSevenDays.data;
      })
      .addCase(getBookingsCreatedInSevenDays.rejected, (state) => {
        state.bookingsCreatedInSevenDaysStatus = LOADING_STATUS.FAILED;
      })

      .addCase(getAccommodationsBookingsAsync.rejected, (state) => {
        state.bookingsItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(updateOwnerBookingAsync.pending, (state) => {
        state.arrivalsAndDeparturesItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(updateOwnerBookingAsync.fulfilled, (state, action) => {
        state.updateBookingStatus = LOADING_STATUS.IDLE;
        state.updatedBookingItem = action.payload;
      })
      .addCase(updateOwnerBookingAsync.rejected, (state) => {
        state.updateBookingStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectBookingItems = (state: RootState) =>
  state.bookings.bookingItems;

export const selectBookingItem = (state: RootState) =>
  state.bookings.bookingItem;

export const selectExtraBookingItems = (state: RootState) =>
  state.bookings.extraBookingItems;

export const selectArrivalsAndDeparturesItem = (state: RootState) =>
  state.bookings.arrivalsAndDeparturesItem;

export const selectBookingItemsStartingInSevenDays = (state: RootState) =>
  state.bookings.bookingItemsStartingInSevenDays;

export const selectBookingItemsCreatedInSevenDays = (state: RootState) =>
  state.bookings.bookingItemsCreatedInSevenDays;

export const selectAccommodationsBookingItems = (
  state: RootState,
  accommodationId: string,
) => {
  return (
    state.bookings.accommodationsBookingItems as AccommodationBookingItem[]
  ).find((a) => a.accommodationId === accommodationId)?.bookingItems;
};

export const selectAccommodationsCalendarBookingItems = (
  state: RootState,
  accommodationId: string,
) => {
  return (
    state.bookings
      .accommodationsCalendarBookingItems as AccommodationBookingItem[]
  ).find((a) => a.accommodationId === accommodationId)?.bookingItems;
};

export const selectBookingItemsLoadingStatus = (state: RootState) =>
  state.bookings.bookingsItemsStatus;

export const selectBookingItemLoadingStatus = (state: RootState) =>
  state.bookings.bookingItemStatus;

export const selectArrivalsAndDeparturesItemsStatus = (state: RootState) =>
  state.bookings.arrivalsAndDeparturesItemsStatus;

export const selectBookingsStartingInSevenDaysStatus = (state: RootState) =>
  state.bookings.bookingsStartingInSevenDaysStatus;

export const selectBookingsCreatedInSevenDaysStatus = (state: RootState) =>
  state.bookings.bookingsCreatedInSevenDaysStatus;

export const selectUpdateBookingStatus = (state: RootState) =>
  state.bookings.updateBookingStatus;
export const { clearExtraBookingItems } = bookingsSlice.actions;
export default bookingsSlice.reducer;
